import { escapeRegExp, startCase } from 'lodash-es';
import { useTranslation } from 'next-i18next/pages';
import { FAQPageJsonLd } from 'next-seo';
import { renderToStaticMarkup } from 'react-dom/server';

import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { isDefined } from '@pflegia/util/functions/type-guards';

export type FaqObject = {
  question: React.ReactNode | string;
  answer: React.ReactNode | string;
  questionForSchema?: string;
  answerForSchema?: string;
};

interface FaqPageJsonLdBaseProps {
  translationNamespace?: string;
  faqs: (string | FaqObject)[];
  translatedJobTitle: string;
  location: string;
  relatedJobTitles?: { id: string; title: string }[];
  relatedLocations?: string[];
  processedSchemaUrls?: Record<string, string | Record<string, string>>;
  values?: Record<string, string | number | undefined>;
}

/**
 * Replaces placeholder tags in an FAQ answer with their plain-text link label.
 *
 * next-seo HTML-escapes every JSON-LD string on output, so any markup placed here
 * (e.g. an <a> tag) would render as literal escaped tag soup in the final schema —
 * the schema text must stay plain text.
 *
 * - If the answer contains <tag>Some text</tag>, it becomes "Some text".
 * - If the answer contains <tag/> or <tag>, it becomes "tag".
 *
 * Example:
 *   replaceSchemaLinks("Visit <jobBoardLink/> for more job positions", { jobBoardLink: "https://www.pflegia.de/job-board" })
 *   "Visit Job Board Link for more job positions"
 */
const replaceSchemaLinks = (answerText: string, schemaLinks: Record<string, Record<string, string> | string>) => {
  let result = answerText;
  // Ignore special characters in an answer so it can be used safely in RegExp
  const replaceTag = (tag: string, makeAnchor: (innerText?: string) => string) => {
    result = result.replace(new RegExp(`<${escapeRegExp(tag)}>(.*?)<\\/${escapeRegExp(tag)}>`, 'g'), (_, innerText) =>
      makeAnchor(innerText),
    );
    result = result.replace(new RegExp(`<${escapeRegExp(tag)}\\s*\\/?>`, 'g'), () => makeAnchor());
  };
  Object.entries(schemaLinks || {}).forEach(([key, value]) => {
    if (typeof value === 'string') replaceTag(key, innerText => innerText || startCase(key));
    else if (Array.isArray(value))
      value.forEach((_url, i) => replaceTag(`${key}${i || ''}`, innerText => `${innerText || startCase(key)}${i || ''}`));
    else if (value && typeof value === 'object') {
      Object.entries(value).forEach(([subKey]) => replaceTag(subKey, innerText => innerText || subKey));
      replaceTag(key, innerText => {
        const links = Object.entries(value).map(([subKey]) => innerText || subKey);
        return links.join(', ');
      });
    }
  });
  return result;
};

interface UseFaqMainEntityProps {
  faqs: (string | FaqObject)[];
  translationNamespace?: string;
  translatedJobTitle: string;
  location: string;
  relatedJobTitles?: { id: string; title: string }[];
  relatedLocations?: string[];
  processedSchemaUrls?: Record<string, string | Record<string, string>>;
  values?: Record<string, string | number | undefined>;
}

export const useFaqMainEntity = ({
  faqs,
  translationNamespace = 'jobBoard',
  translatedJobTitle,
  location,
  relatedJobTitles,
  relatedLocations,
  processedSchemaUrls,
  values = {},
}: UseFaqMainEntityProps) => {
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'questionsAndAnswers' });
  const { sourceDomainName } = useFlavorFromRoute();

  return faqs.reduce(
    (acc, faq) => {
      if (typeof faq === 'string') {
        if (faq === 'otherPopularLocations' && !relatedLocations?.length) return acc;
        if (faq === 'otherPopularJobTitles' && !relatedJobTitles?.length) return acc;

        const translationValues = { translatedJobTitle, location, domain: sourceDomainName, ...values };
        const questionName = t(`${faq}.question`, { ...translationValues, interpolation: { escapeValue: false } });
        const acceptedAnswerText = replaceSchemaLinks(
          t(`${faq}.answer`, { ...translationValues, interpolation: { escapeValue: false } }),
          processedSchemaUrls || {},
        );

        acc.push({ questionName, acceptedAnswerText });
      } else if (isDefined(faq) && typeof faq === 'object') {
        const { question, answer, questionForSchema, answerForSchema } = faq;
        const processedQuestion =
          questionForSchema ?? (typeof question === 'string' ? question : renderToStaticMarkup(question as React.ReactElement));
        const rawAnswer = answerForSchema ?? (typeof answer === 'string' ? answer : renderToStaticMarkup(answer as React.ReactElement));
        const processedAnswer = replaceSchemaLinks(rawAnswer, processedSchemaUrls || {});
        acc.push({ questionName: processedQuestion, acceptedAnswerText: processedAnswer });
      }
      return acc;
    },
    [] as { questionName: string; acceptedAnswerText: string }[],
  );
};

const FaqPageJsonLdBase: React.FC<FaqPageJsonLdBaseProps> = ({
  translationNamespace,
  translatedJobTitle,
  location,
  faqs,
  relatedJobTitles,
  relatedLocations,
  processedSchemaUrls,
  values,
}) => {
  const mainEntity = useFaqMainEntity({
    faqs,
    translationNamespace,
    translatedJobTitle,
    location,
    relatedJobTitles,
    relatedLocations,
    processedSchemaUrls,
    values,
  });

  if (!faqs.length) return null;

  return <FAQPageJsonLd mainEntity={mainEntity} />;
};

export default FaqPageJsonLdBase;
